import { useState } from 'react';
import cn from 'classnames';
import { ButtonShell } from '@round/ui-kit';
import { creatorbase } from '@round/api';
import { ActionCard, assets } from '@round/whitelabel-ui';
import HeaderSummary from 'Modules/Plans/components/HeaderSummary/HeaderSummary';
import useProjectDetails from '../../useProjectDetails';
import ProjectSongChart from '../ProjectSongChart/ProjectSongChart';
import ProjectSongDropdown from '../ProjectSongDropdown/ProjectSongDropdown';
import styles from './SongCard.module.css';
import CreateSongModal from 'Modules/Plans/Songs/Modals/CreateSongModal/CreateSongModal';
import { useUpdateProjectMutation } from '@round/creatorbase-helpers/api-hooks';
import { showNotification } from 'helpers';

const { Plus: PlusIcon, Pencil: EditIcon, Song: SongIcon } = assets;

type Props = {
    className?: string;
    platform?: creatorbase.Platform;
    onSongClick?: (songId: number) => void;
};

const SongCard = ({ className, onSongClick }: Props) => {
    const { project, isLoading: isProjectLoading } = useProjectDetails();

    const { update: updateProject } = useUpdateProjectMutation();

    const [isSongDropdownOpen, setIsSongDropdownOpen] = useState(false);
    const [songDropdownAnchor, setSongDropdownAnchor] = useState<HTMLDivElement | HTMLButtonElement | null>(null);

    // Capture the projectId for which we are creating a song so a project
    // refetch that returns undefined between create and onSuccess can't
    // strand the new song unlinked.
    const [pendingProjectId, setPendingProjectId] = useState<number | null>(null);

    const handleCreatedSong = async (song: creatorbase.Song) => {
        if (pendingProjectId === null) {
            return;
        }

        try {
            await updateProject({ id: pendingProjectId, song_id: song.id });
            showNotification('Song linked to project', 'info');
        } catch {
            showNotification('Failed to link song to project', 'error');
        }
    };

    if (isProjectLoading) {
        return (
            <HeaderSummary.Item className={className}>
                <HeaderSummary.Label>Song stats</HeaderSummary.Label>
                <HeaderSummary.Value className={styles.addSongContainer}>
                    <ActionCard Icon={SongIcon} title="Song stats" isLoading={true} className={styles.addSongCard} />
                </HeaderSummary.Value>
            </HeaderSummary.Item>
        );
    }

    return (
        <>
            <HeaderSummary.Item className={className}>
                <HeaderSummary.Label>Song stats</HeaderSummary.Label>
                {!project?.song_id ? (
                    <HeaderSummary.Value className={styles.addSongContainer}>
                        <ActionCard
                            ref={setSongDropdownAnchor}
                            title="Song"
                            Icon={PlusIcon}
                            onClick={() => setIsSongDropdownOpen(true)}
                            className={styles.addSongCard}
                        />
                    </HeaderSummary.Value>
                ) : (
                    <div className={styles.songStatsContainer}>
                        <HeaderSummary.Value
                            className={cn(styles.value, styles.songChartWrapper, {
                                [styles.pointer]: !!project?.song_id,
                            })}
                            onClick={() => {
                                if (project?.song_id != null) {
                                    onSongClick?.(project.song_id);
                                }
                            }}
                        >
                            <ProjectSongChart key={project?.song_id} songId={project?.song_id} />
                        </HeaderSummary.Value>

                        <ButtonShell
                            ref={setSongDropdownAnchor}
                            onClick={() => setIsSongDropdownOpen(true)}
                            className={cn(styles.dropdownButton, { [styles.active]: isSongDropdownOpen })}
                        >
                            <EditIcon />
                        </ButtonShell>
                    </div>
                )}

                <ProjectSongDropdown
                    songId={project?.song_id}
                    isOpen={isSongDropdownOpen}
                    onOpenChange={setIsSongDropdownOpen}
                    anchor={songDropdownAnchor}
                    onCreateSong={() => {
                        if (project?.id != null) {
                            setPendingProjectId(project.id);
                        }
                    }}
                />
            </HeaderSummary.Item>

            <CreateSongModal
                isOpen={pendingProjectId !== null}
                onClose={() => setPendingProjectId(null)}
                onSuccess={handleCreatedSong}
            />
        </>
    );
};

export default SongCard;
